/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import { Tooltip, IconButton, Menu, MenuItem } from "@mui/material";
import { moreIconButtonSxProps, menuSlotProps } from "../../helpers";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IIPInterface } from "../../../store/api/deviceConfig/types";
import { setSelectedIPInterface, setIsAddNewIPInterface } from "../../../store/api/deviceConfig/reducers";
import { useStoreDispatch } from "../../../store/hooks";
import { setBasicDialogOpenFor } from "../../../store/helpers/helperReducers";
import { useIPInterfaceData } from "./hooks";
import { DIALOGOPENFOR } from "../../../globals/globals";
import { useSelector } from "react-redux";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";

interface IIPInterfaceActionMenuProps {
  tableRow: any;
}

const IPInterfaceActionMenu: React.FC<IIPInterfaceActionMenuProps> = ({ tableRow }) => {
  const dispatch = useStoreDispatch();

  const { permissions, deleteIPInterfaceHandler, anchorElIP, setAnchorElIP } = useIPInterfaceData(tableRow);
  const openMenuIP = Boolean(anchorElIP);
  const selectedDevice = useSelector(selectedDeviceSelector);
  const isOfflineDevice = selectedDevice?.configurationStatus?.toLowerCase() === "offline";

  const handleOnIPMoreIconClick = (event: React.MouseEvent<HTMLButtonElement>, selectedRow: IIPInterface) => {
    setAnchorElIP(event.currentTarget);
    dispatch(setSelectedIPInterface(selectedRow));
    dispatch(setIsAddNewIPInterface(false));
  };

  const editIPInterfaceHandler = () => {
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.IPINTERFACE));
    dispatch(setIsAddNewIPInterface(false));
  };

  const handlePopup = (type: string) => {
    setAnchorElIP(null);
    if (type === "Edit") {
      return editIPInterfaceHandler();
    } else {
      return deleteIPInterfaceHandler();
    }
  };

  return (
    permissions?.hasOpCodeGroup("manage_topology") && !isOfflineDevice && (
      <>
        <Tooltip title="IP Interface Settings" arrow>
          <IconButton
            sx={moreIconButtonSxProps}
            aria-label="more"
            id="ip-interface-setting"
            aria-controls={openMenuIP ? "ip-interface-menu" : undefined}
            aria-expanded={openMenuIP ? "true" : undefined}
            aria-haspopup="true"
            onClick={(event) => handleOnIPMoreIconClick(event, tableRow)}>
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
        <Menu
          id="ip-interface-menu"
          anchorEl={anchorElIP}
          open={openMenuIP}
          onClose={() => setAnchorElIP(null)}
          MenuListProps={{
            "aria-labelledby": "ip-interface-setting",
          }}
          slotProps={menuSlotProps}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
          <MenuItem onClick={() => handlePopup("Edit")}>Edit IP Interface</MenuItem>
          <MenuItem onClick={() => handlePopup("Delete")}>Delete IP Interface</MenuItem>
        </Menu>
      </>
    )
  );
};

export default IPInterfaceActionMenu;
